import React, { useEffect } from 'react';
import { EditorContent, useEditor } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Link from '@tiptap/extension-link';
import Underline from '@tiptap/extension-underline';
import {
  Bold,
  Heading2,
  Italic,
  Link2,
  List,
  ListOrdered,
  Redo2,
  RemoveFormatting,
  Underline as UnderlineIcon,
  Undo2,
} from 'lucide-react';
import { Button } from '../../ui/button';
import { normalizeEmailBodyHtml } from './emailBodyFormatters';

function ToolbarButton({ active = false, onClick, title, children, disabled = false }) {
  return (
    <Button
      type="button"
      variant="outline"
      size="sm"
      title={title}
      disabled={disabled}
      onClick={onClick}
      className={[
        'h-9 min-w-9 border-slate-300 bg-white px-2 text-slate-700 hover:bg-slate-100',
        'dark:border-[#3b3b48] dark:bg-[#16161a] dark:text-slate-100 dark:hover:bg-[#202028]',
        active ? 'border-aqua bg-aqua/10 text-slate-950 dark:border-aqua dark:bg-aqua/20 dark:text-white' : '',
      ].join(' ')}
    >
      {children}
    </Button>
  );
}

const EmailRichTextEditor = ({
  value,
  onChange,
}) => {
  const editor = useEditor({
    immediatelyRender: false,
    extensions: [
      StarterKit.configure({
        heading: { levels: [2, 3] },
      }),
      Underline,
      Link.configure({
        openOnClick: false,
        autolink: true,
        defaultProtocol: 'https',
        HTMLAttributes: {
          rel: 'noopener noreferrer nofollow',
          target: '_blank',
        },
      }),
    ],
    content: normalizeEmailBodyHtml(value),
    editorProps: {
      attributes: {
        class:
          'email-rich-editor ProseMirror min-h-[220px] w-full rounded-b-xl px-4 py-4 text-[15px] leading-7 text-slate-900 focus:outline-none dark:text-white',
      },
    },
    onUpdate: ({ editor: nextEditor }) => {
      onChange(nextEditor.getHTML());
    },
  });

  useEffect(() => {
    if (!editor) return;
    const normalized = normalizeEmailBodyHtml(value);
    const current = editor.getHTML();
    if (normalized !== current) {
      editor.commands.setContent(normalized || '<p></p>', false);
    }
  }, [editor, value]);

  const setLink = () => {
    if (!editor) return;
    const previousUrl = editor.getAttributes('link').href || '';
    const url = window.prompt('Enter link URL', previousUrl);
    if (url === null) return;
    const normalized = url.trim();
    if (!normalized) {
      editor.chain().focus().unsetLink().run();
      return;
    }
    editor.chain().focus().extendMarkRange('link').setLink({ href: normalized }).run();
  };

  return (
    <div className="overflow-hidden rounded-xl border border-slate-300 bg-white shadow-sm dark:border-[#3b3b48] dark:bg-[#16161a]">
      <div className="flex flex-wrap gap-2 border-b border-slate-200 bg-slate-50 px-3 py-3 dark:border-[#2a2a32] dark:bg-[#1b1b23]">
        <ToolbarButton
          title="Bold"
          active={editor?.isActive('bold')}
          onClick={() => editor?.chain().focus().toggleBold().run()}
        >
          <Bold className="h-4 w-4" />
        </ToolbarButton>
        <ToolbarButton
          title="Italic"
          active={editor?.isActive('italic')}
          onClick={() => editor?.chain().focus().toggleItalic().run()}
        >
          <Italic className="h-4 w-4" />
        </ToolbarButton>
        <ToolbarButton
          title="Underline"
          active={editor?.isActive('underline')}
          onClick={() => editor?.chain().focus().toggleUnderline().run()}
        >
          <UnderlineIcon className="h-4 w-4" />
        </ToolbarButton>
        <ToolbarButton
          title="Heading"
          active={editor?.isActive('heading', { level: 2 })}
          onClick={() => editor?.chain().focus().toggleHeading({ level: 2 }).run()}
        >
          <Heading2 className="h-4 w-4" />
        </ToolbarButton>
        <ToolbarButton
          title="Bulleted list"
          active={editor?.isActive('bulletList')}
          onClick={() => editor?.chain().focus().toggleBulletList().run()}
        >
          <List className="h-4 w-4" />
        </ToolbarButton>
        <ToolbarButton
          title="Numbered list"
          active={editor?.isActive('orderedList')}
          onClick={() => editor?.chain().focus().toggleOrderedList().run()}
        >
          <ListOrdered className="h-4 w-4" />
        </ToolbarButton>
        <ToolbarButton
          title="Add link"
          active={editor?.isActive('link')}
          onClick={setLink}
        >
          <Link2 className="h-4 w-4" />
        </ToolbarButton>
        <ToolbarButton
          title="Clear formatting"
          onClick={() => editor?.chain().focus().unsetAllMarks().clearNodes().run()}
        >
          <RemoveFormatting className="h-4 w-4" />
        </ToolbarButton>
        <ToolbarButton
          title="Undo"
          disabled={!editor?.can().undo()}
          onClick={() => editor?.chain().focus().undo().run()}
        >
          <Undo2 className="h-4 w-4" />
        </ToolbarButton>
        <ToolbarButton
          title="Redo"
          disabled={!editor?.can().redo()}
          onClick={() => editor?.chain().focus().redo().run()}
        >
          <Redo2 className="h-4 w-4" />
        </ToolbarButton>
      </div>
      <div className="bg-white dark:bg-[#16161a]">
        <EditorContent editor={editor} />
      </div>
    </div>
  );
};

export default EmailRichTextEditor;
